import React, { useEffect, useRef, useState } from "react";
import {
  X,
  ArrowRight,
  CheckCircle2,
  Copy,
  Check,
  ToggleLeft,
  ToggleRight,
  TrendingUp,
  Target,
  LifeBuoy,
  Wrench,
  Globe,
} from "lucide-react";
import { WORKSHOP_STEPS } from "../data/workshopData";

// ... [KEEP TOKENS AND UI COMPONENTS (Button, PageHeader, Card) EXACTLY AS THEY WERE] ...

// ---- TOKENS (Design System) ----
const TOKENS = {
  colors: {
    ink: "#0B1A3C",
    muted: "#64748B",
    line: "#E2E8F0",
    bg: "#F8FAFC",
    card: "#FFFFFF",
    brand: "#10B981", // Emerald
    brandLight: "#ECFDF5",
    brandDark: "#064E3B",
    danger: "#EF4444",
    dangerLight: "#FEF2F2",
    dangerDark: "#991B1B",
    info: "#3B82F6",
    infoLight: "#EFF6FF",
    infoDark: "#1E3A8A",
  },
  radii: { sm: "6px", md: "12px", lg: "16px" },
  shadows: {
    sm: "0 1px 2px rgba(0,0,0,0.05)",
    lg: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  },
};

// ---- UI COMPONENTS ----

const Button = ({ variant = "primary", disabled, onClick, children }) => {
  const [hover, setHover] = useState(false);
  const base = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 20px",
    borderRadius: TOKENS.radii.sm,
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    border: "none",
    transition: "all 0.2s",
    outline: "none",
    opacity: disabled ? 0.5 : 1,
  };
  const styles = {
    primary: { background: TOKENS.colors.brand, color: "#FFF" },
    secondary: {
      background: "#FFF",
      border: `1px solid ${TOKENS.colors.line}`,
      color: TOKENS.colors.ink,
    },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...base,
        ...styles[variant],
        transform: hover && !disabled ? "translateY(-1px)" : "none",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {children}
    </button>
  );
};

const PageHeader = ({ title, description, actions }) => (
  <header
    style={{
      padding: "24px 40px",
      borderBottom: `1px solid ${TOKENS.colors.line}`,
      backgroundColor: "#FFF",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      position: "sticky",
      top: 0,
      zIndex: 10,
    }}
  >
    <div>
      <p
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: TOKENS.colors.brand,
          textTransform: "uppercase",
          marginBottom: 4,
        }}
      >
        Leadership Workshop
      </p>
      <h2
        style={{
          fontSize: 24,
          fontWeight: 700,
          color: TOKENS.colors.ink,
          margin: 0,
        }}
      >
        {title}
      </h2>
      {description && (
        <p
          style={{
            fontSize: 14,
            color: TOKENS.colors.muted,
            margin: "4px 0 0 0",
          }}
        >
          {description}
        </p>
      )}
    </div>
    <div>{actions}</div>
  </header>
);

const Card = ({ children }) => (
  <div
    style={{
      backgroundColor: TOKENS.colors.card,
      border: `1px solid ${TOKENS.colors.line}`,
      borderRadius: TOKENS.radii.md,
      boxShadow: TOKENS.shadows.sm,
      padding: 32,
      marginBottom: 32,
    }}
  >
    {children}
  </div>
);

// ---- VISUALS ----

// ... [Keep VisualFunnel, VisualComparison, VisualToggles, VisualCriteria] ...
const VisualFunnel = () => (
  <div
    style={{
      height: 250,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 20,
    }}
  >
    <div
      style={{
        width: 100,
        height: 100,
        borderRadius: "50%",
        border: `2px dashed ${TOKENS.colors.muted}`,
        background: "#F1F5F9",
        display: "grid",
        placeContent: "center",
        textAlign: "center",
        fontSize: 11,
        color: TOKENS.colors.muted,
      }}
    >
      2,600
      <br />
      SKUs
    </div>
    <ArrowRight color={TOKENS.colors.muted} />
    <div
      style={{
        width: 0,
        height: 0,
        borderTop: `50px solid ${TOKENS.colors.muted}`,
        borderLeft: "30px solid transparent",
        borderRight: "30px solid transparent",
      }}
    />
    <ArrowRight color={TOKENS.colors.muted} />
    <div
      style={{
        width: 90,
        height: 90,
        background: TOKENS.colors.brandLight,
        borderRadius: 12,
        border: `1px solid ${TOKENS.colors.brand}`,
        display: "grid",
        placeContent: "center",
        textAlign: "center",
        fontSize: 12,
        fontWeight: 700,
        color: TOKENS.colors.brandDark,
      }}
    >
      15-20
      <br />
      Strategic
    </div>
  </div>
);

const VisualComparison = () => (
  <div style={{ display: "flex", gap: 20, minHeight: 200 }}>
    <div
      style={{
        flex: 1,
        borderRadius: 8,
        border: `1px solid ${TOKENS.colors.danger}`,
        background: TOKENS.colors.dangerLight,
        padding: 20,
      }}
    >
      <div
        style={{
          color: TOKENS.colors.danger,
          fontWeight: 700,
          marginBottom: 12,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <X size={16} /> NOT THIS
      </div>
      <ul
        style={{
          paddingLeft: 16,
          color: TOKENS.colors.dangerDark,
          fontSize: 13,
          lineHeight: 2,
        }}
      >
        <li>Teach everything</li>
        <li>Mirror product catalog</li>
        <li>Build one-off launch training</li>
      </ul>
    </div>
    <div
      style={{
        flex: 1,
        borderRadius: 8,
        border: `1px solid ${TOKENS.colors.brand}`,
        background: TOKENS.colors.brandLight,
        padding: 20,
      }}
    >
      <div
        style={{
          color: TOKENS.colors.brandDark,
          fontWeight: 700,
          marginBottom: 12,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Check size={16} /> THIS
      </div>
      <ul
        style={{
          paddingLeft: 16,
          color: TOKENS.colors.brandDark,
          fontSize: 13,
          lineHeight: 2,
        }}
      >
        <li>Teach what drives revenue</li>
        <li>Role relevance</li>
        <li>Reusable module templates</li>
      </ul>
    </div>
  </div>
);

const VisualToggles = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 16,
      alignItems: "center",
      justifyContent: "center",
      height: 200,
    }}
  >
    <div
      style={{
        display: "flex",
        gap: 12,
        alignItems: "center",
        width: "100%",
        maxWidth: 400,
        justifyContent: "space-between",
        padding: 12,
        border: `1px solid ${TOKENS.colors.brand}`,
        background: TOKENS.colors.brandLight,
        borderRadius: 8,
      }}
    >
      <span style={{ fontWeight: 700, color: TOKENS.colors.brandDark }}>
        Account Executives
      </span>
      <ToggleRight size={24} color={TOKENS.colors.brand} />
    </div>
    <div
      style={{
        display: "flex",
        gap: 12,
        alignItems: "center",
        width: "100%",
        maxWidth: 400,
        justifyContent: "space-between",
        padding: 12,
        border: `1px solid ${TOKENS.colors.brand}`,
        background: TOKENS.colors.brandLight,
        borderRadius: 8,
      }}
    >
      <span style={{ fontWeight: 700, color: TOKENS.colors.brandDark }}>
        Product Specialists
      </span>
      <ToggleRight size={24} color={TOKENS.colors.brand} />
    </div>
    <div
      style={{
        display: "flex",
        gap: 12,
        alignItems: "center",
        width: "100%",
        maxWidth: 400,
        justifyContent: "space-between",
        padding: 12,
        border: `1px solid ${TOKENS.colors.line}`,
        background: TOKENS.colors.bg,
        borderRadius: 8,
        opacity: 0.6,
      }}
    >
      <span style={{ fontWeight: 600, color: TOKENS.colors.muted }}>
        Customer Success
      </span>
      <ToggleLeft size={24} color={TOKENS.colors.muted} />
    </div>
  </div>
);

const VisualCriteria = () => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
    {[
      "Revenue Impact",
      "Strategic Growth",
      "Complexity Risk",
      "Launch Priority",
    ].map((c) => (
      <div
        key={c}
        style={{
          padding: 16,
          background: TOKENS.colors.infoLight,
          border: `1px solid ${TOKENS.colors.info}`,
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div
          style={{
            width: 20,
            height: 20,
            background: TOKENS.colors.info,
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Check size={14} color="#FFF" />
        </div>
        <span style={{ fontWeight: 600, color: TOKENS.colors.infoDark }}>
          {c}
        </span>
      </div>
    ))}
    {["Field Requests", "Exec Pet Project"].map((c) => (
      <div
        key={c}
        style={{
          padding: 16,
          background: TOKENS.colors.bg,
          border: `1px solid ${TOKENS.colors.line}`,
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          gap: 12,
          opacity: 0.6,
        }}
      >
        <div
          style={{
            width: 20,
            height: 20,
            border: `2px solid ${TOKENS.colors.muted}`,
            borderRadius: 4,
          }}
        />
        <span style={{ fontWeight: 600, color: TOKENS.colors.muted }}>{c}</span>
      </div>
    ))}
  </div>
);

// --- NEW VISUAL: THE DATA HUNTER ---
const VisualDataHunter = () => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
    {/* REVOPS CARD */}
    <div
      style={{
        padding: 20,
        border: `1px solid ${TOKENS.colors.line}`,
        borderRadius: 12,
        background: "#FFF",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <div style={{ padding: 8, background: "#ECFDF5", borderRadius: 6 }}>
          <TrendingUp size={20} color="#059669" />
        </div>
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: TOKENS.colors.muted,
              textTransform: "uppercase",
            }}
          >
            RevOps / Finance
          </div>
          <div
            style={{ fontSize: 13, fontWeight: 700, color: TOKENS.colors.ink }}
          >
            Past Performance
          </div>
        </div>
      </div>
      <div
        style={{ fontSize: 12, color: TOKENS.colors.muted, marginBottom: 8 }}
      >
        <strong>The Ask:</strong> "Trailing 12-month revenue by Product SKU."
      </div>
      <div
        style={{
          fontSize: 11,
          background: TOKENS.colors.bg,
          padding: 6,
          borderRadius: 4,
          color: TOKENS.colors.muted,
        }}
      >
        Apply 80/20 rule to find drivers.
      </div>
    </div>

    {/* PRODUCT CARD */}
    <div
      style={{
        padding: 20,
        border: `1px solid ${TOKENS.colors.line}`,
        borderRadius: 12,
        background: "#FFF",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <div style={{ padding: 8, background: "#EFF6FF", borderRadius: 6 }}>
          <Target size={20} color="#2563EB" />
        </div>
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: TOKENS.colors.muted,
              textTransform: "uppercase",
            }}
          >
            Product Leadership
          </div>
          <div
            style={{ fontSize: 13, fontWeight: 700, color: TOKENS.colors.ink }}
          >
            Future Strategy
          </div>
        </div>
      </div>
      <div
        style={{ fontSize: 12, color: TOKENS.colors.muted, marginBottom: 8 }}
      >
        <strong>The Ask:</strong> "Which 3 products are tied to CEO's FY26
        OKRs?"
      </div>
      <div
        style={{
          fontSize: 11,
          background: TOKENS.colors.bg,
          padding: 6,
          borderRadius: 4,
          color: TOKENS.colors.muted,
        }}
      >
        Identify "Must-Win" battles.
      </div>
    </div>

    {/* SUPPORT CARD */}
    <div
      style={{
        padding: 20,
        border: `1px solid ${TOKENS.colors.line}`,
        borderRadius: 12,
        background: "#FFF",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <div style={{ padding: 8, background: "#FFF7ED", borderRadius: 6 }}>
          <LifeBuoy size={20} color="#EA580C" />
        </div>
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: TOKENS.colors.muted,
              textTransform: "uppercase",
            }}
          >
            Support Ops
          </div>
          <div
            style={{ fontSize: 13, fontWeight: 700, color: TOKENS.colors.ink }}
          >
            The Pain Index
          </div>
        </div>
      </div>
      <div
        style={{ fontSize: 12, color: TOKENS.colors.muted, marginBottom: 8 }}
      >
        <strong>The Ask:</strong> "Top 10 Ticket Drivers (How-to questions)."
      </div>
      <div
        style={{
          fontSize: 11,
          background: TOKENS.colors.bg,
          padding: 6,
          borderRadius: 4,
          color: TOKENS.colors.muted,
        }}
      >
        Finds high-complexity items.
      </div>
    </div>

    {/* REGIONAL CARD */}
    <div
      style={{
        padding: 20,
        border: `1px dashed ${TOKENS.colors.muted}`,
        borderRadius: 12,
        background: TOKENS.colors.bg,
        opacity: 0.8,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <div style={{ padding: 8, background: "#FFF", borderRadius: 6 }}>
          <Globe size={20} color="#64748B" />
        </div>
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: TOKENS.colors.muted,
              textTransform: "uppercase",
            }}
          >
            Regional Trap
          </div>
          <div
            style={{ fontSize: 13, fontWeight: 700, color: TOKENS.colors.ink }}
          >
            Global vs Local
          </div>
        </div>
      </div>
      <div style={{ fontSize: 12, color: TOKENS.colors.muted }}>
        <strong>Rule:</strong> Define "Global Core" (80%). Let regions build
        their own "Local Variants" (20%).
      </div>
    </div>
  </div>
);

// ... [Keep VisualScoringTable, VisualFinalList, VisualBlueprint] ...
const VisualScoringTable = () => (
  <div
    style={{
      border: `1px solid ${TOKENS.colors.line}`,
      borderRadius: 8,
      overflow: "hidden",
    }}
  >
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "2fr 1fr 1fr 1fr",
        background: TOKENS.colors.bg,
        borderBottom: `1px solid ${TOKENS.colors.line}`,
        padding: 12,
        fontSize: 11,
        fontWeight: 700,
        color: TOKENS.colors.muted,
      }}
    >
      <div>PRODUCT</div>
      <div>SCORE</div>
      <div>AE FIT</div>
      <div>SPEC FIT</div>
    </div>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "2fr 1fr 1fr 1fr",
        padding: 12,
        borderBottom: `1px solid ${TOKENS.colors.line}`,
        alignItems: "center",
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 13 }}>Cloud Security Pro</div>
      <div
        style={{
          width: 60,
          height: 6,
          background: TOKENS.colors.line,
          borderRadius: 3,
        }}
      >
        <div
          style={{
            width: "90%",
            height: "100%",
            background: TOKENS.colors.brand,
            borderRadius: 3,
          }}
        />
      </div>
      <div
        style={{
          fontSize: 10,
          background: TOKENS.colors.infoLight,
          color: TOKENS.colors.infoDark,
          padding: "2px 6px",
          borderRadius: 4,
          width: "fit-content",
        }}
      >
        CORE
      </div>
      <div
        style={{
          fontSize: 10,
          background: TOKENS.colors.infoLight,
          color: TOKENS.colors.infoDark,
          padding: "2px 6px",
          borderRadius: 4,
          width: "fit-content",
        }}
      >
        CORE
      </div>
    </div>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "2fr 1fr 1fr 1fr",
        padding: 12,
        alignItems: "center",
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 13 }}>Legacy Connector</div>
      <div
        style={{
          width: 60,
          height: 6,
          background: TOKENS.colors.line,
          borderRadius: 3,
        }}
      >
        <div
          style={{
            width: "40%",
            height: "100%",
            background: "#F59E0B",
            borderRadius: 3,
          }}
        />
      </div>
      <div style={{ fontSize: 10, color: TOKENS.colors.muted }}>None</div>
      <div
        style={{
          fontSize: 10,
          background: "#FEF3C7",
          color: "#B45309",
          padding: "2px 6px",
          borderRadius: 4,
          width: "fit-content",
        }}
      >
        REF
      </div>
    </div>
  </div>
);

const VisualFinalList = () => (
  <div style={{ display: "flex", gap: 16 }}>
    <div
      style={{
        flex: 1,
        border: `1px solid ${TOKENS.colors.brand}`,
        background: TOKENS.colors.brandLight,
        borderRadius: 8,
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: TOKENS.colors.brandDark,
          marginBottom: 8,
          textTransform: "uppercase",
        }}
      >
        Onboarding Core (Locked)
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: TOKENS.colors.ink,
          marginBottom: 4,
        }}
      >
        5 Products Selected
      </div>
      <div
        style={{
          height: 4,
          background: "rgba(16, 185, 129, 0.2)",
          borderRadius: 2,
        }}
      >
        <div
          style={{
            width: "60%",
            height: "100%",
            background: TOKENS.colors.brand,
            borderRadius: 2,
          }}
        />
      </div>
    </div>
    <div
      style={{
        flex: 1,
        border: `1px solid ${TOKENS.colors.info}`,
        background: TOKENS.colors.infoLight,
        borderRadius: 8,
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: TOKENS.colors.infoDark,
          marginBottom: 8,
          textTransform: "uppercase",
        }}
      >
        Launch Modules
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: TOKENS.colors.ink,
          marginBottom: 4,
        }}
      >
        2 Products Selected
      </div>
      <div
        style={{
          height: 4,
          background: "rgba(59, 130, 246, 0.2)",
          borderRadius: 2,
        }}
      >
        <div
          style={{
            width: "30%",
            height: "100%",
            background: TOKENS.colors.info,
            borderRadius: 2,
          }}
        />
      </div>
    </div>
  </div>
);

const VisualBlueprint = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    <div
      style={{
        padding: 12,
        border: `1px dashed ${TOKENS.colors.muted}`,
        borderRadius: 8,
        background: TOKENS.colors.bg,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: TOKENS.colors.muted,
          marginBottom: 4,
        }}
      >
        TEMPLATE
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: TOKENS.colors.ink }}>
        Standard Product Module
      </div>
    </div>
    <div style={{ textAlign: "center" }}>
      <ArrowRight
        size={16}
        style={{ transform: "rotate(90deg)" }}
        color={TOKENS.colors.muted}
      />
    </div>
    <div style={{ display: "flex", gap: 12 }}>
      <div
        style={{
          flex: 1,
          padding: 12,
          border: `1px solid ${TOKENS.colors.brand}`,
          background: TOKENS.colors.brandLight,
          borderRadius: 8,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: TOKENS.colors.brandDark,
          }}
        >
          AE ONBOARDING
        </div>
        <div style={{ fontSize: 12 }}>5 Core Products</div>
      </div>
      <div
        style={{
          flex: 1,
          padding: 12,
          border: `1px solid ${TOKENS.colors.info}`,
          background: TOKENS.colors.infoLight,
          borderRadius: 8,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: TOKENS.colors.infoDark,
          }}
        >
          SPECIALIST PATH
        </div>
        <div style={{ fontSize: 12 }}>8 Deep Dives</div>
      </div>
    </div>
  </div>
);

const Visual = ({ id }) => {
  switch (id) {
    case 0:
      return <VisualFunnel />;
    case 1:
      return <VisualComparison />;
    case 2:
      return <VisualToggles />;
    case 3:
      return <VisualCriteria />;
    case 4:
      return <VisualDataHunter />; // UPDATED STEP 4
    case 5:
      return <VisualScoringTable />;
    case 6:
      return <VisualFinalList />;
    case 7:
      return <VisualBlueprint />;
    default:
      return (
        <div
          style={{
            height: 200,
            display: "grid",
            placeContent: "center",
            color: TOKENS.colors.muted,
          }}
        >
          Interactive Visual Placeholder
        </div>
      );
  }
};

// ---- MAIN MODAL LOGIC ----

export default function StrategicWorkshopModal({ onClose }) {
  const [activeStep, setActiveStep] = useState(0);
  const dialogRef = useRef(null);

  const current = WORKSHOP_STEPS[activeStep] ?? {};

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleCopy = async () => {
    if (!current?.copyBlock?.text) return;
    try {
      await navigator.clipboard.writeText(current.copyBlock.text);
      alert("Copied!");
    } catch {}
  };

  const isLast = activeStep === WORKSHOP_STEPS.length - 1;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        display: "grid",
        placeItems: "center",
        backgroundColor: "rgba(0,0,0,0.4)",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        ref={dialogRef}
        style={{
          width: "95%",
          height: "90vh",
          backgroundColor: "#FFF",
          borderRadius: TOKENS.radii.lg,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: TOKENS.shadows.lg,
          outline: "none",
        }}
      >
        {/* Header */}
        <PageHeader
          title="Strategic Product Selection"
          description="Facilitation flow to go from catalog to a focused, revenue-driving curriculum."
          actions={
            <Button variant="secondary" onClick={onClose}>
              <X size={16} /> Close
            </Button>
          }
        />

        {/* Body */}
        <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
          {/* Stepper (Left Rail) */}
          <aside
            style={{
              width: 300,
              borderRight: `1px solid ${TOKENS.colors.line}`,
              backgroundColor: TOKENS.colors.bg,
              padding: 24,
              overflowY: "auto",
            }}
          >
            <nav style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {WORKSHOP_STEPS.map((step, i) => (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(i)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 16px",
                    borderRadius: TOKENS.radii.sm,
                    textAlign: "left",
                    background: activeStep === i ? "#FFF" : "transparent",
                    border:
                      activeStep === i
                        ? `1px solid ${TOKENS.colors.line}`
                        : "1px solid transparent",
                    boxShadow: activeStep === i ? TOKENS.shadows.sm : "none",
                    cursor: "pointer",
                  }}
                >
                  <span
                    style={{
                      display: "grid",
                      placeContent: "center",
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      fontSize: 12,
                      fontWeight: 700,
                      background:
                        activeStep > i
                          ? TOKENS.colors.brand
                          : activeStep === i
                          ? TOKENS.colors.ink
                          : TOKENS.colors.line,
                      color:
                        activeStep === i || activeStep > i
                          ? "#FFF"
                          : TOKENS.colors.muted,
                    }}
                  >
                    {activeStep > i ? <Check size={14} /> : i + 1}
                  </span>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: activeStep === i ? 600 : 400,
                      color:
                        activeStep === i
                          ? TOKENS.colors.ink
                          : TOKENS.colors.muted,
                    }}
                  >
                    {step.title}
                  </span>
                </button>
              ))}
            </nav>
          </aside>

          {/* Main Content (Right Area) */}
          <main style={{ flex: 1, overflowY: "auto", padding: 40 }}>
            <div style={{ maxWidth: 800, margin: "0 auto" }}>
              <div style={{ marginBottom: 32 }}>
                <h1
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: TOKENS.colors.ink,
                    marginBottom: 12,
                  }}
                >
                  {current?.headline}
                </h1>
                <p
                  style={{
                    fontSize: 16,
                    lineHeight: 1.6,
                    color: TOKENS.colors.muted,
                  }}
                >
                  {current?.content}
                </p>
              </div>

              <Card>
                <div
                  style={{
                    textAlign: "center",
                    textTransform: "uppercase",
                    fontSize: 11,
                    fontWeight: 700,
                    color: TOKENS.colors.muted,
                    marginBottom: 20,
                  }}
                >
                  Facilitation Visual
                </div>
                <Visual id={current?.id} />
              </Card>

              {current?.copyBlock && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                    borderRadius: TOKENS.radii.md,
                    border: `1px solid ${TOKENS.colors.brand}`,
                    backgroundColor: TOKENS.colors.brandLight,
                    padding: 20,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: TOKENS.colors.brandDark,
                        textTransform: "uppercase",
                        marginBottom: 4,
                      }}
                    >
                      {current.copyBlock.label}
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        fontStyle: "italic",
                        color: TOKENS.colors.brandDark,
                      }}
                    >
                      “{current.copyBlock.text}”
                    </div>
                  </div>
                  <Button variant="secondary" onClick={handleCopy}>
                    <Copy size={14} /> Copy
                  </Button>
                </div>
              )}
            </div>
          </main>
        </div>

        {/* Footer */}
        <footer
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
            borderTop: `1px solid ${TOKENS.colors.line}`,
            padding: 20,
            backgroundColor: "#FFF",
          }}
        >
          <Button
            variant="secondary"
            disabled={activeStep === 0}
            onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
          >
            Back
          </Button>
          <Button
            onClick={() => {
              if (!isLast)
                setActiveStep((s) =>
                  Math.min(WORKSHOP_STEPS.length - 1, s + 1)
                );
              else onClose?.();
            }}
          >
            {isLast ? "Finish Workshop" : "Next Step"} <ArrowRight size={16} />
          </Button>
        </footer>
      </div>
    </div>
  );
}
